import styled from '@emotion/styled'
import { Minus, Plus, Rumble } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { Media, MediaContextProvider, mediaStyle } from 'app/theme/media'
import { useVocabularyData } from 'app/utils/vocabulary'
import React, { memo, useState } from 'react'

import { Props as ServiceProps, Service } from './Service'

export interface Props {
  languageCode: string
  services: ServiceProps[]
  title?: string
}

export const RoomsServices = memo(function RoomsServices({
  languageCode,
  services,
  title,
}: Props) {
  if (services.length < 1) {
    return null
  }

  const [expanderStatus, setExpanderStatus] = useState(false)

  return (
    <Container>
      <Wrapper>
        <Rumble />

        {title ? <Title>{title}</Title> : null}

        <style type="text/css">{mediaStyle}</style>
        <MediaContextProvider>
          <Media lessThan="ipadHorizontal">
            <Expander
              dial={5}
              onClick={() =>
                expanderStatus
                  ? setExpanderStatus(false)
                  : setExpanderStatus(true)
              }
              row
            >
              {useVocabularyData('show', languageCode)}
              {expanderStatus ? <Minus /> : <Plus />}
            </Expander>
          </Media>
        </MediaContextProvider>

        <Services className={expanderStatus ? 'open' : undefined} row wrap>
          {services.map((item, index) => (
            <Service key={index} {...item} />
          ))}
        </Services>
      </Wrapper>
    </Container>
  )
})

const Container = styled.section`
  background: ${({ theme }) => theme.colors.variants.neutralLight2};
  padding: 13.75rem 11.944vw 12.5rem;
  text-align: center;

  @media (max-width: 1199px) {
    padding-right: 2rem;
    padding-left: 2rem;
  }

  @media (max-width: 1023px) {
    padding: 6.6875rem 0 6.25rem;
  }
`

const Wrapper = styled.div`
  background: ${({ theme }) => theme.colors.variants.neutralLight4};
  padding: 6.25rem 9.931vw 8.125rem;
  position: relative;

  > svg {
    width: 2.5rem;
    height: 2.5rem;
    fill: ${({ theme }) => theme.colors.variants.secondaryLight};
    position: absolute;
    top: -1.25rem;
    left: 50%;
    transform: translateX(-50%);
  }

  @media (max-width: 1023px) {
    background: ${({ theme }) => theme.colors.variants.primaryLight};
    padding: 3.75rem 2rem 2.3125rem;
  }
`

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.variants.secondaryLight};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.5rem;
  font-weight: 500;
  line-height: 1.75rem;

  @media (max-width: 1023px) {
    color: ${({ theme }) => theme.colors.variants.neutralLight4};
    font-size: 1.25rem;
    line-height: 1.5rem;
  }
`

const Expander = styled(FlexBox)`
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.075rem;
  line-height: 1.125rem;
  margin: 2.1875rem auto 0;
  text-transform: uppercase;

  svg {
    width: auto;
    height: 0.75rem;
    margin-left: 0.625rem;
    stroke: ${({ theme }) => theme.colors.variants.neutralLight4};
    stroke-width: 2;
  }
`

const Services = styled(FlexBox)`
  @media (max-width: 1023px) {
    display: none;
    margin-top: 2.5rem;
    padding: 0;
    &.open {
      display: block;
    }
  }
`
